import { Filter } from "@/components/layout/types";
import { formatCurrencyValue, formatDateValue } from "@/lib/utils";
import { FieldDataType, OptionsResponse } from "@/types/dynamic-detail";
import { ObjectListColumn } from "../components/layout-2/types2";
import { renderField } from "./field-renderer";
import { formatHumanDuration } from "./format-time";

// Generate auto column from field name
export function generateAutoColumn<T>(fieldName: string, fieldsInfo?: Record<string, any> | null): ObjectListColumn<T> {
    const formatField = (value: any) => {
        const formatted = value.replace("__", "'s ").replace("_", " ");
        // Capitalize first letter and letters after spaces (but not after apostrophes)
        return formatted.replace(/(^|\s)\w/g, (match: string) => match.toUpperCase());
    };
    const fieldInfo = fieldsInfo?.[fieldName];
    const label = fieldInfo?.label || formatField(fieldName);
    const type = fieldInfo?.type;

    return {
        name: label,
        description: fieldInfo?.description || undefined,
        accessorFn: (instance: T) => {
            const value = (instance as any)[fieldName];

            // Handle different value types
            if (value === null || value === undefined) {
                return "-";
            }

            if (typeof value === "object") {
                // For objects, try to display a readable representation
                if (value.hasOwnProperty("value") && value.hasOwnProperty("type")) {
                    return renderField(
                        {
                            value: value.value,
                            field_type: value.type
                        } as FieldDataType,
                        { layout: "flex" }
                    );
                }
                if (value.name) {
                    return value.name;
                }
                if (value.full_name) {
                    return value.full_name;
                }
                if (value.email) {
                    return value.email;
                }
                if (value.first_name || value.last_name) {
                    return `${value.first_name || ""} ${value.last_name || ""}`.trim();
                }
                if (value.representation) {
                    return value.representation;
                }
                // Fallback for objects without obvious property
                return "[Object]";
            }

            if (type === "time") {
                return formatHumanDuration(value);
            }

            if (typeof value === "boolean") {
                return value ? "Yes" : "No";
            }

            if (typeof value === "number") {
                if (type === "currency") {
                    return formatCurrencyValue(value);
                }
                return value.toString();
            }

            if (typeof value === "string") {
                // Handle ISO dates
                if (fieldName.includes("date") || fieldName.endsWith("_at") || type === "datetime" || type === "date") {
                    try {
                        const date = new Date(value);
                        if (!isNaN(date.getTime())) {
                            return formatDateValue(date, "long");
                        }
                    } catch {
                        // If conversion fails, return original string
                    }
                }
                return value;
            }

            // For all other types, convert to string
            return String(value);
        },
        // Add navigation for relational fields (objects with href)
        navigateTo: (instance: T) => {
            const value = (instance as any)[fieldName];

            // Check if this is a relational field with href
            if (value && typeof value === "object" && value.href) {
                return value.href;
            }

            return undefined;
        },
        fieldName: fieldName
    };
}

// Generate auto filters from fields info
export function generateAutoFilters(metadata?: OptionsResponse | null): Filter[] {
    const fieldsInfo = {
        ...(metadata?.actions?.POST || {}),
        ...(metadata?.list_filters || {})
    };
    const filters: Filter[] = [];
    for (const fieldName in fieldsInfo || {}) {
        const fieldInfo = fieldsInfo?.[fieldName];
        const label = fieldInfo?.label || fieldName;
        const description = fieldInfo?.description || undefined;

        // Determine filter type based on backend metadata
        if (fieldInfo?.type === "boolean") {
            filters.push({
                type: "checkbox",
                field_name: fieldName,
                name: label,
                description
            });
        }

        if (fieldInfo?.type === "choice" && fieldInfo?.choices) {
            filters.push({
                type: "select",
                field_name: fieldName,
                name: label,
                description,
                options: fieldInfo.choices.map((choice: any) => ({
                    value: choice.value,
                    label: choice.display_name
                }))
            });
        }

        if (fieldInfo?.type === "picklist") {
            console.log("🔄 PICKLIST", `/${metadata?.object_url}/picklists/${fieldName}`);
            filters.push({
                type: "combobox",
                field_name: fieldName,
                name: label,
                description,
                optionsUrl: `/crm/objects/${metadata?.object_url}/picklists/${fieldName}/`,
                optionFormatter(o: { display_name: string; value: string }) {
                    return {
                        value: o.value,
                        label: o.display_name
                    };
                }
            });
        }

        if (fieldInfo?.type === "datetime") {
            filters.push({
                type: "datetime-range",
                field_name: fieldName,
                name: label,
                description
            });
        }

        if (fieldInfo?.type === "date") {
            filters.push({
                type: "date-range",
                field_name: fieldName,
                name: label,
                description
            });
        }

        if (fieldInfo?.type === "integer" || fieldInfo?.type === "float") {
            filters.push({
                type: "number",
                field_name: fieldName,
                name: label,
                description
            });
        }

        if (fieldInfo?.type === "string" || fieldInfo?.type === "text") {
            filters.push({
                type: "text",
                field_name: fieldName,
                name: label,
                description
            });
        }
    }
    return filters;
}

// Merge manual columns with simple columns
export function mergeColumns<T>(simpleColumns: string[] = [], fieldsInfo?: Record<string, any> | null): ObjectListColumn<T>[] {
    const simpleColumnsGenerated = simpleColumns.map(fieldName => generateAutoColumn<T>(fieldName, fieldsInfo));
    return [...simpleColumnsGenerated];
}

// Filter and reorder columns according to visibleColumns
export function getFilteredColumns<T>(visibleColumns: string[], allColumns: ObjectListColumn<T>[], fieldsInfo?: Record<string, any> | null): ObjectListColumn<T>[] {
    const result: ObjectListColumn<T>[] = [];

    for (const fieldName of visibleColumns || []) {
        // Look for existing column in allColumns
        const existingColumn = allColumns.find(col => col.fieldName === fieldName);

        if (existingColumn) {
            // Use existing column (with custom logic)
            result.push(existingColumn);
        } else {
            // Auto-generate basic column
            result.push(generateAutoColumn<T>(fieldName, fieldsInfo));
        }
    }

    return result;
}
